'use client';

import { css, cx } from '@onbeam/business-styled-system/css';
import { flex } from '@onbeam/business-styled-system/patterns';
import { breakout } from '@/styles';
import { MaximizeIcon, MutedIcon, VolumeIcon } from '@/icons';
import { useRef } from 'react';
import { NewsVideoActionButton } from './NewsVideoActionButton';
import { useState } from 'react';

export const NewsVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleFullscreen = () => {
    const video = videoRef.current as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
    };

    if (!video) return;

    const requestFullscreen =
      video.requestFullscreen?.bind(video) ||
      video.webkitEnterFullscreen?.bind(video);

    if (!requestFullscreen) {
      console.warn('Fullscreen API is not supported.');
      return;
    }

    try {
      requestFullscreen();
      video.muted = false;
      setIsMuted(false);
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
    }
  };

  return (
    <div
      className={cx(
        breakout(),
        css({
          position: 'relative',
          maxH: '[15rem]',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'opacity.md',
          overflow: 'hidden',
          mt: '16',
          aspectRatio: '16/9',
          mb: '10',
          md: {
            rounded: 'xs',
            w: 'full',
            mt: '24',
            maxH: '[30rem]',
            border: '1px solid',
            borderColor: 'opacity.md',
          },
        }),
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        playsInline
        muted
        src="https://d1sbvhbby74rp9.cloudfront.net/optimised.mp4"
        className={css({
          position: 'absolute',
          top: '[50%]',
          left: '[50%]',
          transform: 'translate(-50%, -50%)',
          w: 'full',
          h: 'auto',
          aspectRatio: '16/9',
          bg: 'mono.senary',
        })}
      />
      <div
        className={flex({
          position: 'absolute',
          bottom: '3',
          right: '3',
          gap: '2',
        })}
      >
        <NewsVideoActionButton
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          onClick={toggleMute}
        >
          {isMuted ? (
            <MutedIcon height={16} width={16} className={css({ zIndex: 1 })} />
          ) : (
            <VolumeIcon height={16} width={16} className={css({ zIndex: 1 })} />
          )}
        </NewsVideoActionButton>
        <NewsVideoActionButton
          aria-label="Open video in full screen"
          onClick={handleFullscreen}
        >
          <MaximizeIcon height={16} width={16} className={css({ zIndex: 1 })} />
        </NewsVideoActionButton>
      </div>
    </div>
  );
};
